import { ApolloLink, type Operation, type FetchResult, Observable } from '@apollo/client/core'
import { type FormattedExecutionResult, print, type GraphQLError } from 'graphql'
import { createClient, type ClientOptions, type Client, type Sink } from 'graphql-ws'

interface RestartableClient extends Client {
  restart: () => void
}

function createRestartableClient (options: ClientOptions): RestartableClient {
  let restartRequested = false
  let restart = () => {
    restartRequested = true
  }

  const client = createClient({
    ...options,
    on: {
      ...options.on,
      opened: (socket) => {
        options.on?.opened?.(socket)

        restart = () => {
          if ((socket as WebSocket).readyState === WebSocket.OPEN) {
            // if the socket is still open for the restart, do the restart
            ; (socket as WebSocket).close(4205, 'Client Restart')
          } else {
            // otherwise the socket might've closed, indicate that you want
            // a restart on the next opened event
            restartRequested = true
          }
        }

        // just in case you were eager to restart
        if (restartRequested) {
          restartRequested = false
          restart()
        }
      }
    }
  })

  return {
    ...client,
    restart: () => { restart() }
  }
}

export class WebSocketLink extends ApolloLink {
  readonly client: RestartableClient

  constructor (options: ClientOptions) {
    super()
    this.client = createRestartableClient(options)
  }

  public request (operation: Operation): Observable<FetchResult> {
    return new Observable((sink) => {
      return this.client.subscribe<FetchResult>(
        { ...operation, query: print(operation.query) },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: (err) => {
            if (err instanceof Error) {
              sink.error(err); return
            }

            if (err instanceof CloseEvent) {
              sink.error(
                // reason will be available on clean closes
                new Error(
                  `Socket closed with event ${err.code} ${err.reason || ''}`
                )
              )
              return
            }

            sink.error(
              new Error(
                (err as GraphQLError[])
                  .map(({ message }) => message)
                  .join(', ')
              )
            )
          }
          // casting around a wrong type in graphql-ws, which incorrectly expects `Sink<ExecutionResult>`
        } satisfies Sink<FormattedExecutionResult> as any
      )
    })
  }
}
